import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Heading, HStack, Icon, IconButton, SimpleGrid, Spinner, Text, VStack} from '@chakra-ui/react';
import {ArrowLeft, BarChart3, UserMinus} from 'lucide-react';
import {useNavigate, useParams} from 'react-router-dom';
import {Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {useApi} from '@/uses/useApi';
import {useLanguage} from '@/contexts/languageContext';
import {Button, PollExclusions} from '@/components';
import demoPoll from './demoPoll.json';

/** Front-only demo poll (hardcoded JSON, no DB) — pinned at the top of the poll list */
export const DEMO_POLL_ID = demoPoll.id;

const BAR_COLORS = ['#F54D2B', '#2B6CB0', '#38A169', '#D69E2E', '#805AD5', '#DD6B20', '#319795', '#E53E3E'];

const CHOICE_TYPES = ['radio', 'select', 'checkbox'];

/** Field props (label/choices) may be stored either already localized or as a {lang: value} object */
const localize = (value, language) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return value[language] ?? [];
    return value;
};

const StatCard = ({label, value, color = '#2B6CB0'}) => (
    <Box bg="white" borderRadius="lg" p={4} border="1px solid" borderColor="gray.100">
        <Text fontSize="sm" color="gray.500" fontWeight="medium">{label}</Text>
        <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
    </Box>
);

/** Horizontal bar chart + per-choice count/percentage list */
const ChoiceResult = ({data, total, t}) => {
    if (data.length === 0) {
        return <Text color="gray.400">{t('polls.noAnswersYet')}</Text>;
    }
    const chartHeight = Math.max(120, data.length * 46);
    return (
        <VStack align="stretch" gap={3} w="100%">
            <Box w="100%" h={`${chartHeight}px`}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} layout="vertical" margin={{top: 0, right: 24, bottom: 0, left: 0}}>
                        <XAxis type="number" allowDecimals={false} hide/>
                        <YAxis type="category" dataKey="name" width={160} tick={{fontSize: 13}}/>
                        <Tooltip cursor={{fill: 'rgba(0,0,0,0.04)'}}/>
                        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
                            {data.map((entry, i) => (
                                <Cell key={`cell-${i}`} fill={BAR_COLORS[i % BAR_COLORS.length]}/>
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            <VStack align="stretch" gap={1}>
                {data.map((entry, i) => (
                    <HStack key={`legend-${i}`} justify="space-between">
                        <HStack gap={2} overflow="hidden">
                            <Box boxSize="10px" borderRadius="2px" bg={BAR_COLORS[i % BAR_COLORS.length]}/>
                            <Text fontSize="sm" isTruncated>{entry.name}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                            {entry.value} ({total > 0 ? Math.round((entry.value / total) * 100) : 0}%)
                        </Text>
                    </HStack>
                ))}
            </VStack>
        </VStack>
    );
};

const TextResult = ({values, t}) => {
    if (values.length === 0) {
        return <Text color="gray.400">{t('polls.noAnswersYet')}</Text>;
    }
    return (
        <VStack align="stretch" gap={2} maxH="320px" overflowY="auto" w="100%">
            {values.map((v, i) => (
                <Box key={`txt-${i}`} bg="gray.50" borderRadius="md" px={3} py={2}>
                    <Text fontSize="sm" whiteSpace="pre-wrap">{v}</Text>
                </Box>
            ))}
        </VStack>
    );
};

const NumberResult = ({values, t}) => {
    const nums = values.map(Number).filter((n) => !Number.isNaN(n));
    if (nums.length === 0) {
        return <Text color="gray.400">{t('polls.noAnswersYet')}</Text>;
    }
    const sum = nums.reduce((acc, n) => acc + n, 0);
    const avg = Math.round((sum / nums.length) * 100) / 100;
    return (
        <SimpleGrid columns={{base: 1, md: 3}} gap={3} w="100%">
            <StatCard label={t('polls.average')} value={avg}/>
            <StatCard label={t('polls.min')} value={Math.min(...nums)} color="#38A169"/>
            <StatCard label={t('polls.max')} value={Math.max(...nums)} color="#DD6B20"/>
        </SimpleGrid>
    );
};

/** Builds the {name, value} dataset for a choice field, seeding every choice at 0 */
const buildChoiceData = (field, values, language, t) => {
    const choices = localize(field.choices, language) ?? [];
    const counts = new Map(choices.map((c) => [c, 0]));
    /** checkbox answers are arrays, single-choice answers are scalars */
    const flat = values.flatMap((v) => (Array.isArray(v) ? v : [v]));
    let others = 0;
    for (const v of flat) {
        if (v === undefined || v === null || v === '') continue;
        if (counts.has(v)) {
            counts.set(v, counts.get(v) + 1);
        } else {
            others += 1;
        }
    }
    const data = [...counts.entries()].map(([name, value]) => ({name, value}));
    if (others > 0) data.push({name: t('polls.otherAnswers'), value: others});
    return data;
};

export const PollResults = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {language, t} = useLanguage();
    const api = useApi();

    const [poll, setPoll] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [excludedUsers, setExcludedUsers] = useState([]);
    const [excludedAnswersNumber, setExcludedAnswersNumber] = useState(0);
    const [showExclusions, setShowExclusions] = useState(false);
    const [loading, setLoading] = useState(true);

    const isDemo = id === DEMO_POLL_ID;

    /**
     * Les résultats viennent d'un endpoint dédié : c'est l'API qui écarte les réponses
     * des participants exclus, le rapprochement réponse ↔ utilisateur n'étant possible
     * que côté serveur (clé chiffrée).
     */
    /** Poll affiché : une réponse arrivant après un changement de sondage est ignorée */
    const displayedId = useRef(id);
    const loadResults = useCallback(() => {
        displayedId.current = id;
        return api.ApiService.rawGet(`/poll/${id}/results`)
            .then((res) => {
                if (res?.data?.type !== 'success' || displayedId.current !== id) return;
                const value = res?.data?.value ?? {};
                setPoll(value?.poll ?? null);
                setAnswers(value?.answers ?? []);
                setExcludedUsers(value?.excludedUsers ?? []);
                setExcludedAnswersNumber(value?.excludedAnswersNumber ?? 0);
            })
            .catch((e) => console.log(e?.response?.data));
    }, [id]);

    useEffect(() => {
        /** Demo poll: everything comes from the hardcoded JSON, no API call */
        if (isDemo) {
            setPoll(demoPoll);
            setAnswers(demoPoll.answers ?? []);
            setLoading(false);
            return;
        }

        setLoading(true);
        loadResults().finally(() => {
            if (displayedId.current === id) setLoading(false);
        });
    }, [id, isDemo, loadResults]);

    const fields = useMemo(() => poll?.fields?.[language] ?? [], [poll, language]);
    const title = useMemo(() => {
        const value = poll?.title;
        return (value && typeof value === 'object' ? value[language] : value) ?? '';
    }, [poll, language]);

    const renderField = (field) => {
        const label = localize(field.label, language) || '';
        const values = answers
            .map((a) => a?.fields?.[field.id])
            .filter((v) => v !== undefined && v !== null && v !== '');
        const respondents = values.length;

        let body;
        if (CHOICE_TYPES.includes(field.type)) {
            body = <ChoiceResult data={buildChoiceData(field, values, language, t)} total={respondents} t={t}/>;
        } else if (field.type === 'number') {
            body = <NumberResult values={values} t={t}/>;
        } else {
            body = <TextResult values={values} t={t}/>;
        }

        /** Optional free-text comments left by respondents on this question */
        const comments = answers
            .map((a) => a?.comments?.[field.id])
            .filter((c) => typeof c === 'string' && c.trim() !== '');

        return (
            <Box key={field.id} bg="white" borderRadius="xl" p={6} shadow="sm" border="1px solid" borderColor="gray.100" w="100%">
                <HStack justify="space-between" align="start" mb={4} gap={4}>
                    <Heading size="md" color="secondary.500">{label}</Heading>
                    <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">{t('polls.totalResponses', {count: respondents})}</Text>
                </HStack>
                {body}
                {comments.length > 0 && (
                    <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.100">
                        <Text fontSize="sm" fontWeight="semibold" color="gray.500" mb={2}>
                            {t('polls.comments')} ({comments.length})
                        </Text>
                        <VStack align="stretch" gap={2} maxH="240px" overflowY="auto">
                            {comments.map((c, i) => (
                                <Box key={`cmt-${i}`} bg="gray.50" borderRadius="md" px={3} py={2} borderLeft="3px solid" borderColor="rgba(245,77,43,0.4)">
                                    <Text fontSize="sm" whiteSpace="pre-wrap">{c}</Text>
                                </Box>
                            ))}
                        </VStack>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <VStack w="100%" h="100%" align="stretch" overflowY="auto" p={{base: 4, md: 8}} gap={6}>
            <HStack gap={4} align="center">
                <IconButton aria-label={t('actions.back')} variant="iconOnly" onClick={() => navigate('/poll')}>
                    <Icon as={ArrowLeft}/>
                </IconButton>
                <VStack align="start" gap={0} flex={1} overflow="hidden">
                    <HStack gap={2} color="secondary.500">
                        <Icon as={BarChart3}/>
                        <Text fontSize="sm" fontWeight="medium">{t('polls.results')}</Text>
                    </HStack>
                    <Heading size="lg" color="secondary.500" isTruncated>{title}</Heading>
                </VStack>
                <VStack align="end" gap={0}>
                    <Text fontWeight="bold" color="secondary.500" whiteSpace="nowrap">
                        {t('polls.totalResponses', {count: answers.length})}
                    </Text>
                    {excludedAnswersNumber > 0 && <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                        {t('polls.excludedResponses', {count: excludedAnswersNumber})}
                    </Text>}
                </VStack>
                {!isDemo && <Button
                    visual="icon"
                    title={t('polls.excludedParticipants')}
                    {...(showExclusions || excludedUsers.length > 0 ? {bg: 'secondary.500', color: 'white'} : {})}
                    onClick={() => setShowExclusions((prevState) => !prevState)}
                >
                    <Icon as={UserMinus} boxSize={5}/>
                </Button>}
            </HStack>

            {!isDemo && !loading && showExclusions && <PollExclusions
                pollId={id}
                excludedUsers={excludedUsers}
                onChange={loadResults}
            />}

            {
                loading
                    ? <HStack justify="center" py={20}><Spinner/></HStack>
                    : answers.length === 0
                        ? <Box bg="white" borderRadius="xl" p={10} textAlign="center" border="1px solid" borderColor="gray.100">
                            <Text color="gray.400">
                                {excludedAnswersNumber > 0 ? t('polls.allAnswersExcluded') : t('polls.noAnswersYet')}
                            </Text>
                        </Box>
                        : fields.length === 0
                            ? <Text color="gray.400">{t('polls.noFields')}</Text>
                            : <VStack align="stretch" gap={5}>{fields.map(renderField)}</VStack>
            }
        </VStack>
    );
};

export default PollResults;
